import { useEffect, useMemo, useState } from 'react'
import { useTicketSubscriptionsQuery } from '~/generated/graphql'
import { customPropertyId, setCustomProperty } from '~/lib/techtouch'
import { excludePathes } from '~/contexts/AuthContext'
import { useRouter } from 'next/router'

type CustomPropertiesSet = Record<(typeof customPropertyId)[keyof typeof customPropertyId], boolean>

/**
 * techtouchのカスタムプロパティを設定するカスタムフックです
 *
 * カスタムプロパティのidはtechtouchの管理画面で設定したものを使います。
 * techtouch.tsに定義されているcustomPropertyIdを使ってください。
 * @see utils/techtouch.ts
 *
 * 設定したカスタムプロパティの確認方法（開発者ツールのコンソールに出力して確認できます。）
    TechtouchAdmin.customProperty.keys().forEach(function (key) {
      console.log(key)
      console.log(TechtouchAdmin.customProperty.get(key))
      console.log(typeof TechtouchAdmin.customProperty.get(key))
    })
 */
export const useSetCustomProperties = () => {
  const [customPropertiesSet, setCustomPropertiesSet] = useState<CustomPropertiesSet>(
    Object.values(customPropertyId).reduce((prev, val) => {
      prev[val] = false
      return prev
    }, {} as CustomPropertiesSet),
  )

  const router = useRouter()

  // 認証ページだったらtrueを返す
  const isAuthPage = useMemo(() => excludePathes.some((path) => router.pathname.startsWith(path)), [router.pathname])

  // 全てのカスタムプロパティがセットされているか確認
  const allPropertiesSet = useMemo(
    () => Object.values(customPropertiesSet).every((value) => value),
    [customPropertiesSet],
  )

  // GraphQLでデータを取得する時にはskipにcustomPropertiesSetの値を渡す(無駄なデータ取得を防ぐ)
  const { data: ticketSubscriptionData } = useTicketSubscriptionsQuery({
    skip: isAuthPage || customPropertiesSet[customPropertyId.canCancelSubscriptionId],
  })

  const canCancelSubscription = ticketSubscriptionData?.viewer.canCancelSubscription

  useEffect(() => {
    // 全てのプロパティがセットされているまたは認証ページの場合、早期リターン
    if (allPropertiesSet || isAuthPage) {
      return
    }

    try {
      // テックタッチ側で「テキストが一致している」という条件を使っているのでString型にする必要がある。
      const properties = [
        {
          id: customPropertyId.canCancelSubscriptionId,
          value: canCancelSubscription === false ? 'false' : canCancelSubscription === true ? 'true' : undefined,
        },
      ]

      properties.forEach(({ id, value }) => {
        if (value !== undefined && !customPropertiesSet[id]) {
          setCustomProperty(id, value)
          setCustomPropertiesSet((prevState) => ({ ...prevState, [id]: true }))
        }
      })
    } catch (error) {
      console.error('Failed to set custom properties:', error)
    }
  }, [ticketSubscriptionData, canCancelSubscription, customPropertiesSet, allPropertiesSet, isAuthPage])
}
